// Horizontal scrollable rail showing the lowest fare per airline. Tap a
// chip → applies that airline as the only selected filter and scrolls down
// to the offer list. Data is derived purely from offers already in memory.

import { useMemo } from "react";
import { Airplane } from "@phosphor-icons/react";
import { AIRLINE_LOGOS, formatPrice } from "@/lib/api";

export default function AirlinePriceRail({
  offers = [],
  selectedAirlineCode,   // null = "all"
  onSelectAirline,       // (code | null) => void
  tripType = "round_trip",
}) {
  const cheapestByAirline = useMemo(() => {
    const map = new Map();
    for (const o of offers) {
      if (!o.airline_code || typeof o.price !== "number") continue;
      const prev = map.get(o.airline_code);
      if (!prev || o.price < prev.price) {
        map.set(o.airline_code, {
          code: o.airline_code,
          name: o.airline_name,
          price: o.price,
          currency: o.currency || "USD",
        });
      }
    }
    return [...map.values()].sort((a, b) => a.price - b.price);
  }, [offers]);

  if (cheapestByAirline.length === 0) return null;

  const handleTap = (code) => {
    // Toggle: tapping the selected chip again clears the filter.
    onSelectAirline(selectedAirlineCode === code ? null : code);
  };

  const tripLabel = tripType === "round_trip" ? "Round-trip" : "One-way";

  return (
    <div className="md:hidden mt-4" data-testid="airline-rail">
      <p className="text-base font-semibold text-[#1C1C1C] leading-tight">Lowest price by airline</p>
      <p className="text-xs text-[#666460] mt-0.5">Cheapest live {tripLabel.toLowerCase()} fare per carrier</p>

      <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-5 px-5 mt-3 pb-1 snap-x">
        {cheapestByAirline.map((a) => {
          const active = selectedAirlineCode === a.code;
          return (
            <button
              key={a.code}
              type="button"
              onClick={() => handleTap(a.code)}
              data-testid={`airline-chip-${a.code}`}
              className="shrink-0 snap-start text-center w-[84px]"
            >
              <div
                className={`w-14 h-14 rounded-full mx-auto bg-white flex items-center justify-center overflow-hidden border-2 transition-colors ${
                  active ? "border-[#C29B62] ring-2 ring-[#C29B62]/30" : "border-[#E0DDD6]"
                }`}
              >
                {AIRLINE_LOGOS[a.code] ? (
                  <img
                    src={AIRLINE_LOGOS[a.code]}
                    alt={a.name}
                    className="w-9 h-9 object-contain"
                    loading="lazy"
                  />
                ) : (
                  <Airplane size={18} weight="duotone" className="text-[#C29B62]" />
                )}
              </div>
              <p
                className={`mt-2 font-semibold text-sm ${
                  active ? "text-[#C29B62]" : "text-[#1C1C1C]"
                }`}
              >
                {formatPrice(a.price, a.currency)}
              </p>
              <p className="text-[10px] tracking-wider text-[#666460] uppercase truncate">{a.code}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
